import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import Input from "../../../../../../components/Input";
import FileUpload from "../../../../../../components/FileUpload";
import { string } from "../../../../../../constants/string";
import ButtonComp from "../../../../../../components/Button";
import OtpPopup from "../../../../../../components/OtpPopup";
import VerifiedPopup from "../../../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../../../store/useVerifiedStore";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import theme from "../../../../../../theme";
import { usePersonalInfoFormStore } from "../../../../../../store/usePersonalInfoFormStore";
import {
  isValidContactNumber,
  isValidEmail,
} from "../../../../../../utils/validators";
import { useClaimantFormStore } from "../../../../../../store/useClaimantFormStore";
import { useCountryOptions } from "../../../../../../hooks/useCountryOptions";
import { useBranchNames } from "../../../../../../hooks/useBranchNames";
import { useRelationOptions } from "../../../../../../hooks/useRelationOptions";
import { verifyOtp } from "../../../../../../services/guest";
import { useToast } from "../../../../../../hooks/useToast";
import { useOtpVerifiedStore } from "../../../../../../store/useOtpVerifiedStore";
import check from "../../../../../../assets/images/check.svg";
import { useOtp } from "../../../../../../hooks/useOtp";

export default function ClaimantPersonalInfo({
  handleClaimantInfoNext,
  handleBack,
}: {
  handleClaimantInfoNext: (data: any) => void;
  handleBack: () => void;
  isDeathClaim?: boolean;
  handleTenPercentPolicyInfoNext?: (data: any) => void;
}) {
  const { countryOptions } = useCountryOptions();
  const { branchOptions } = useBranchNames();
  const { relationOptions } = useRelationOptions();
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [countryCode, setCountryCode] = useState("+27");
  const [errors, setErrors] = useState<{ [key: string]: string }>({
    email: "",
    contactNumber: "",
    identificationNumber: "",
    title: "",
    firstName: "",
    lastName: "",
    address: "",
    identificationType: "",
    initials: "",
    gender: "",
    dateOfBirth: "",
    agentOrBroker: "",
    sourceOfFunds: "",
    preferredLanguage: "",
    preferredMethodOfCommunication: "",
    nationality: "",
    idExpiryDate: "",
    claimantIdFile: "",
    relationship: "",
    networkProvider: "",
    claimant_id_document: "",
  });

  const showVerified = useVerifiedStore((state) => state.showVerified);
  const setShowVerified = useVerifiedStore((state) => state.setShowVerified);
  const addDocument = useClaimFormStore((state) => state.addDocument);
  const personalInfoForm = usePersonalInfoFormStore(
    (state) => state.personalInfoForm
  );
  const setPersonalInfoForm = usePersonalInfoFormStore(
    (state) => state.setPersonalInfoForm
  );

  const form = useClaimantFormStore((state) => state.claimantForm);
  const setForm = useClaimantFormStore((state) => state.setClaimantForm);
  const { setIsOtpVerified, isOtpVerified } = useOtpVerifiedStore();
  const { documents } = useClaimFormStore();
  const fileName = documents["claimant_id_document"]?.name ?? "No file chosen";

  const cellPhoneNumber = `${countryCode}${form.contactNumber}`;

  const { showInfoToast } = useToast();
  const { sendOtp } = useOtp();

  const countryCodes = [
    { label: "South Africa (+27)", code: "+27" },
    { label: "India (+91)", code: "+91" },
    { label: "USA (+1)", code: "+1" },
    { label: "UK (+44)", code: "+44" },
  ];

  const handleChange = (field: any, value: any) => {
    const updated = { ...form, [field]: value };
    setForm(updated);

    if (!showErrors) return;

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (!value) {
        newErrors[field] = `Please enter ${field
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()}`;
      } else {
        delete newErrors[field];
      }

      if (field === "email" && typeof value === "string") {
        newErrors.email = !isValidEmail(value.trim())
          ? "Invalid email address"
          : "";
      }
      return newErrors;
    });
  };

  const handleOtpVerify = async () => {
    const cellPhoneNumber = `${countryCode}${form.contactNumber}`;
    const res = await verifyOtp(cellPhoneNumber, otpValue);
    if (res.status === 200 && res.data.success === true) {
      setOtpValue("");
      setShowOtp(false);
      setIsOtpVerified(true);
      setShowVerified(true);
    } else if (res.data.success === false) {
      setOtpValue("");
      setShowOtp(false);
      showInfoToast(res.data.message);
    } else {
      showInfoToast(res.data.message);
    }
  };

  const handleVerifiedClose = () => {
    setShowVerified(false);
  };

  const validateDeathClaimForm = () => {
    const requiredFields = [
      "title",
      "firstName",
      "lastName",
      "gender",
      "dateOfBirth",
      "maritalStatus",
      "nationality",
      "contactNumber",
      "email",
      "countryOfResidence",
      "countryOfBirth",
      "address",
      "postalCode",
      "branchName",
      "occupation",
      "beneficiaryEmployer",
      "agentOrBroker",
      "claimant_id_document",
      "initials",
      "relationship",
      "networkProvider",
      "identificationNumber",
      "identificationType",
    ];
    const newErrors: any = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const value = form[field as keyof typeof form];
      if (!value) {
        newErrors[field] = ` Please enter ${field
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()}`;
        isValid = false;
      }
    });

    if (
      typeof form.email === "string" &&
      form.email.trim() &&
      !isValidEmail(form.email.trim())
    ) {
      newErrors.email = "Invalid email address";
      isValid = false;
    }

    if (
      typeof form.contactNumber === "string" &&
      form.contactNumber.trim() &&
      !isValidContactNumber(form.contactNumber.trim())
    ) {
      newErrors.contactNumber = "Invalid contact number (10 digits only)";
      isValid = false;
    }

    if (form.identificationType === "id" && form.identificationNumber) {
      const error = validateSouthAfricanID(form.identificationNumber);
      if (error) {
        newErrors.identificationNumber = error;
        isValid = false;
      }
    }

    if (!documents.claimant_id_document) {
      newErrors.claimant_id_document = "Upload a document";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateSouthAfricanID = (id: string): string | null => {
    // must be 13 digits
    if (!/^\d{13}$/.test(id)) return "ID number must be 13 digits";

    // Extract parts
    const dob = id.substring(0, 6);
    const ssss = parseInt(id.substring(6, 10), 10);
    const citizenship = parseInt(id.charAt(10), 10);
    const checkDigit = id.charAt(12);

    // Validate DOB
    const year = parseInt(dob.substring(0, 2), 10);
    const month = parseInt(dob.substring(2, 4), 10);
    const day = parseInt(dob.substring(4, 6), 10);
    let fullYear = 2000 + year;
    if (new Date(fullYear, month - 1, day) > new Date()) {
      fullYear -= 100;
    }

    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return "Invalid date of birth in ID number";
    }

    // Validate gender sequence
    if (ssss < 0 || ssss > 9999) {
      return "Invalid gender sequence in ID number";
    }

    // Validate citizenship
    if (![0, 1, 2].includes(citizenship)) {
      return "Invalid citizenship digit in ID number";
    }

    // Luhn checksum
    const luhnCheck = (num: string) => {
      let sum = 0;
      let alt = false;
      for (let i = num.length - 1; i >= 0; i--) {
        let n = parseInt(num.charAt(i), 10);
        if (alt) {
          n *= 2;
          if (n > 9) n -= 9;
        }
        sum += n;
        alt = !alt;
      }
      return sum % 10 === 0;
    };

    if (!luhnCheck(id)) return "Invalid ID number checksum";

    return null; // ✅ Valid
  };

  const handleOtpVerification = async () => {
    if (!form.contactNumber) {
      showInfoToast("Please enter your cellphone number before requesting OTP");
      return;
    }

    if (form.contactNumber.length < 8) {
      showInfoToast("Please enter a valid cellphone number");
      return;
    }
    setShowOtp(true);
    const cellPhoneNumber = `${countryCode}${form.contactNumber}`;
    await sendOtp(cellPhoneNumber);
  };

  const handleNext = () => {
    setShowErrors(true);

    if (!isOtpVerified) {
      showInfoToast("Please verify OTP before proceeding.");
      return;
    }
      if (validateDeathClaimForm()) {
        handleClaimantInfoNext(form);
      }
  };


  useEffect(() => {
    if (showErrors) validateDeathClaimForm();
  }, [form]);

  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        setShowVerified(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showVerified]);

  return (
      <Box component="form" noValidate autoComplete="off">
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  select
                  fullWidth
                  label="Title"
                  variant="outlined"
                  value={form.title}
                  onChange={(e: any) => handleChange("title", e.target.value)}
                  error={showErrors && Boolean(errors.title)}
                  helperText={showErrors ? errors.title : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>Select Title</span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem
                    value="Adv"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    ADV
                  </MenuItem>
                  <MenuItem
                    value="Dr"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    DR
                  </MenuItem>
                  <MenuItem
                    value="Miss"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MISS
                  </MenuItem>
                  <MenuItem
                    value="Mr"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MR
                  </MenuItem>
                  <MenuItem
                    value="Mrs"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MRS
                  </MenuItem>
                  <MenuItem
                    value="Ms"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MS
                  </MenuItem>
                  <MenuItem
                    value="Prof"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    PROF
                  </MenuItem>
                  <MenuItem
                    value="Rev"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    REV
                  </MenuItem>
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  fullWidth
                  label="Initials"
                  variant="outlined"
                  value={form.initials}
                  onChange={(e: any) =>
                    handleChange("initials", e.target.value)
                  }
                  error={showErrors && Boolean(errors.initials)}
                  helperText={showErrors ? errors.initials : ""}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  select
                  fullWidth
                  label="Gender"
                  variant="outlined"
                  value={form.gender}
                  onChange={(e: any) => handleChange("gender", e.target.value)}
                  error={showErrors && Boolean(errors.gender)}
                  helperText={showErrors ? errors.gender : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>Select Gender</span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem
                    value="Male"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Male
                  </MenuItem>
                  <MenuItem
                    value="female"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Female
                  </MenuItem>
                  <MenuItem
                    value="other"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Other
                  </MenuItem>
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="First name"
                  variant="outlined"
                  value={form.firstName}
                  onChange={(e: any) =>
                    handleChange("firstName", e.target.value)
                  }
                  error={showErrors && Boolean(errors.firstName)}
                  helperText={showErrors ? errors.firstName : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Last name"
                  variant="outlined"
                  value={form.lastName}
                  onChange={(e: any) =>
                    handleChange("lastName", e.target.value)
                  }
                  error={showErrors && Boolean(errors.lastName)}
                  helperText={showErrors ? errors.lastName : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  type="date"
                  fullWidth
                  label="Date of Birth"
                  placeholder="DD-MM-YYYY"
                  variant="outlined"
                  value={form.dateOfBirth || ""}
                  onChange={(e: any) => {
                    setForm({
                      ...form,
                      dateOfBirth: e.target.value,
                    });
                  }}
                   inputProps={{
    max: new Date().toISOString().split("T")[0],
  }}
                  error={showErrors && Boolean(errors.dateOfBirth)}
                  helperText={showErrors ? errors.dateOfBirth : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  select
                  fullWidth
                  label="Marital status"
                  variant="outlined"
                  value={form.maritalStatus}
                  onChange={(e: any) =>
                    handleChange("maritalStatus", e.target.value)
                  }
                  error={showErrors && Boolean(errors.maritalStatus)}
                  helperText={showErrors ? errors.maritalStatus : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>
                          Select Marital Status
                        </span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem
                    value="divorced"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Divorced
                  </MenuItem>
                  <MenuItem
                    value="married"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Married
                  </MenuItem>
                  <MenuItem
                    value="single"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Single
                  </MenuItem>
                  <MenuItem
                    value="widow"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Widow
                  </MenuItem>
                  <MenuItem
                    value="widower"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    Widower
                  </MenuItem>
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Input
                  select
                  fullWidth
                  label="Nationality"
                  variant="outlined"
                  value={form.nationality}
                  onChange={(e: any) =>
                    handleChange("nationality", e.target.value)
                  }
                  error={showErrors && Boolean(errors.nationality)}
                  helperText={showErrors ? errors.nationality : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>
                          Select Nationality
                        </span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  {countryOptions.length === 0 ? (
                    <MenuItem disabled>Loading nationality...</MenuItem>
                  ) : (
                    countryOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{
                          background: "background.default",
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))
                  )}
                </Input>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Beneficiary's employer"
                  variant="outlined"
                  value={form.beneficiaryEmployer}
                  onChange={(e: any) =>
                    handleChange("beneficiaryEmployer", e.target.value)
                  }
                  error={showErrors && Boolean(errors.beneficiaryEmployer)}
                  helperText={showErrors ? errors.beneficiaryEmployer : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Occupation"
                  variant="outlined"
                  value={form.occupation}
                  onChange={(e: any) =>
                    handleChange("occupation", e.target.value)
                  }
                  error={showErrors && Boolean(errors.occupation)}
                  helperText={showErrors ? errors.occupation : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Email"
                  variant="outlined"
                  value={form.email}
                  onChange={(e: any) => handleChange("email", e.target.value)}
                  error={showErrors && Boolean(errors.email)}
                  helperText={showErrors ? errors.email : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  select
                  fullWidth
                  label="Country of residence"
                  variant="outlined"
                  value={form.countryOfResidence}
                  onChange={(e: any) =>
                    handleChange("countryOfResidence", e.target.value)
                  }
                  error={showErrors && Boolean(errors.countryOfResidence)}
                  helperText={showErrors ? errors.countryOfResidence : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>Select Countries</span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  {countryOptions.length === 0 ? (
                    <MenuItem disabled>Loading countries...</MenuItem>
                  ) : (
                    countryOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{
                          background: "background.default",
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))
                  )}
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} mt={0}>
                <RadioGroup
                  row
                  name="passport"
                  value={form.identificationType}
                  onChange={(e) =>
                    handleChange("identificationType", e.target.value)
                  }
                >
                  <FormControlLabel value="id" control={<Radio />} label="ID" />
                  <FormControlLabel
                    value="passport"
                    control={<Radio />}
                    label="Passsport"
                  />
                </RadioGroup>
                <Input
                  fullWidth
                  variant="outlined"
                  placeholder={
                    form.identificationType === "passport"
                      ? "Passport Number"
                      : "ID Number"
                  }
                  value={form.identificationNumber}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    handleChange("identificationNumber", value);

                    if (form.identificationType === "id") {
                      const error = validateSouthAfricanID(value);
                      setErrors((prev: any) => ({
                        ...prev,
                        identificationNumber: error || "",
                      }));
                    } else {
                      setErrors((prev: any) => {
                        const newErrors = { ...prev };
                        delete newErrors.identificationNumber;
                        return newErrors;
                      });
                    }
                  }}
                  error={showErrors && Boolean(errors.identificationNumber)}
                  helperText={showErrors ? errors.identificationNumber : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} mt={1.8}>
                <Input
                  select
                  fullWidth
                  label="Country of birth"
                  variant="outlined"
                  value={form.countryOfBirth}
                  onChange={(e: any) =>
                    handleChange("countryOfBirth", e.target.value)
                  }
                  error={showErrors && Boolean(errors.countryOfBirth)}
                  helperText={showErrors ? errors.countryOfBirth : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>Select Countries</span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  {countryOptions.length === 0 ? (
                    <MenuItem disabled>Loading countries...</MenuItem>
                  ) : (
                    countryOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{
                          background: "background.default",
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))
                  )}
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Physical/Postal address"
                  variant="outlined"
                  value={form.address}
                  onChange={(e: any) => handleChange("address", e.target.value)}
                  error={showErrors && Boolean(errors.address)}
                  helperText={showErrors ? errors.address : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  select
                  fullWidth
                  label="Network Provider"
                  variant="outlined"
                  value={form.networkProvider}
                  onChange={(e: any) =>
                    handleChange("networkProvider", e.target.value)
                  }
                  error={showErrors && Boolean(errors.networkProvider)}
                  helperText={showErrors ? errors.networkProvider : ""}
                >
                  <MenuItem
                    disabled
                    value=""
                    sx={{ background: "background.default" }}
                  >
                    -Select Network Provider-
                  </MenuItem>
                  <MenuItem
                    value="Vodacom"
                    sx={{
                      background: "background.default",
                      borderBottom: "1px solid #DFDDDD",
                    }}
                  >
                    Vodacom
                  </MenuItem>
                  <MenuItem
                    value="Telcom"
                    sx={{
                      background: "background.default",
                      borderBottom: "1px solid #DFDDDD",
                    }}
                  >
                    Telcom
                  </MenuItem>
                  <MenuItem
                    value="Cell c"
                    sx={{
                      background: "background.default",
                      borderBottom: "1px solid #DFDDDD",
                    }}
                  >
                    Cell c
                  </MenuItem>
                  <MenuItem
                    value="MTN"
                    sx={{ background: "background.default" }}
                  >
                    MTN
                  </MenuItem>
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  select
                  fullWidth
                  label="Relationship to deceased"
                  variant="outlined"
                  value={form.relationship}
                  onChange={(e: any) =>
                    handleChange("relationship", e.target.value)
                  }
                  error={showErrors && Boolean(errors.relationship)}
                  helperText={showErrors ? errors.relationship : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>
                          Select Relationship
                        </span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  {loading ? (
                    <MenuItem disabled>Loading relationships...</MenuItem>
                  ) : (
                    relationOptions.map((relation: string, index) => (
                      <MenuItem
                        key={index}
                        value={relation}
                        sx={{
                          background: "background.default",
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {relation}
                      </MenuItem>
                    ))
                  )}
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  fullWidth
                  label="Postal code"
                  variant="outlined"
                  value={form.postalCode}
                  onChange={(e: any) =>
                    handleChange("postalCode", e.target.value)
                  }
                  error={showErrors && Boolean(errors.postalCode)}
                  helperText={showErrors ? errors.postalCode : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={18}
                  sx={{ display: "block", textAlign: "left" }}
                >
                  Claimant Cellphone number (for OTP verification)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={form.contactNumber}
                  placeholder="Enter Your Cellphone Number"
                  onChange={(e: any) => {
                    setIsOtpVerified(false);
                    handleChange("contactNumber", e.target.value);
                  }}
                  error={showErrors && Boolean(errors.contactNumber)}
                  helperText={showErrors ? errors.contactNumber : ""}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: "60px",
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Select
                          value={countryCode || "+27"}
                          onChange={(e: any) => setCountryCode(e.target.value)}
                          variant="standard"
                          disableUnderline
                          sx={{ width: 100 }}
                        >
                          {countryCodes.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {isOtpVerified ? (
                          <img
                            src={check}
                            alt="check"
                            style={{ height: 25, width: 25 }}
                          />
                        ) : (
                          <ButtonComp
                            variant="contained"
                            width={{ xs: 100, md: 200 }}
                            onClick={handleOtpVerification}
                          >
                            Send OTP
                          </ButtonComp>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid> 
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  select
                  fullWidth
                  label="Branch name"
                  variant="outlined"
                  value={form.branchName}
                  onChange={(e: any) =>
                    handleChange("branchName", e.target.value)
                  }
                  error={showErrors && Boolean(errors.branchName)}
                  helperText={showErrors ? errors.branchName : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>
                          Icebolethu group branches
                        </span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  {branchOptions.length === 0 ? (
                    <MenuItem disabled>Loading branches...</MenuItem>
                  ) : (
                    branchOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        sx={{
                          background: "background.default",
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))
                  )}
                </Input>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: "left" }}>
                <FormControl
                  component="fieldset"
                  error={showErrors && Boolean(errors.agentOrBroker)}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    Communication regarding the claim should take place with:
                  </Typography>
                  <RadioGroup
                    row
                    name="agentOrBroker"
                    value={form.agentOrBroker}
                    onChange={(e) =>
                      handleChange("agentOrBroker", e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="claimant"
                      control={<Radio />}
                      label="Claimant"
                    />
                    <FormControlLabel
                      value="employer"
                      control={<Radio />}
                      label="Employer"
                    />
                    <FormControlLabel
                      value="broker"
                      control={<Radio />}
                      label="Broker"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid size={12} sx={{ textAlign: "left" }}>
                <FormControl component="fieldset" fullWidth>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Source of funds with the following options:
                  </Typography>
                  <RadioGroup
                    row
                    name="sourceoffunds"
                    value={form.sourceOfFunds}
                    onChange={(e) =>
                      handleChange("sourceOfFunds", e.target.value)
                    }
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      columnGap: 4,
                      rowGap: 1,
                    }}
                  >
                    <FormControlLabel
                      value="Grants"
                      control={<Radio />}
                      label="Grants - Disability/Social grant"
                      sx={{ width: "fit-content" }}
                    />
                    <FormControlLabel
                      value="Allowance"
                      control={<Radio />}
                      label="Allowance"
                      sx={{ width: "fit-content" }}
                    />
                    <FormControlLabel
                      value="Salary"
                      control={<Radio />}
                      label="Salary/Wage"
                      sx={{ width: "fit-content" }}
                    />
                    <FormControlLabel
                      value="Pension"
                      control={<Radio />}
                      label="Pension replace with Policy/Fund pay-outs (investment products etc) as a suggestion."
                      sx={{ width: "fit-content", maxWidth: 400 }}
                    />

                    <FormControlLabel
                      value="Passive"
                      control={<Radio />}
                      label="Passive Income (Rental, dividends and interest income)"
                      sx={{ width: "fit-content", maxWidth: 324 }}
                    />
                    <FormControlLabel
                      value="Savings"
                      control={<Radio />}
                      label="Savings/Investments"
                      sx={{ width: "fit-content" }}
                    />
                    <FormControlLabel
                      value="Business"
                      control={<Radio />}
                      label="Business Income"
                      sx={{ width: "fit-content" }}
                    />
                    <FormControlLabel
                      value="Inheritance"
                      control={<Radio />}
                      label="Inheritance/Gifts/Donations"
                      sx={{ width: "fit-content" }}
                    />
                    <FormControlLabel
                      value="Other"
                      control={<Radio />}
                      label="Other"
                      sx={{ width: "fit-content" }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Typography
              variant="body1"
              fontWeight={600}
              mt={2}
              textAlign="left"
            >
              Claimant ID / Passport Copy
            </Typography>
            <FileUpload
              inputId="claimant-id"
              fileName={fileName}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({
                      ...prev,
                      claimant_id_document: "File must be less than 2MB",
                    }));
                    return;
                  }
                  addDocument("claimant_id_document", file);
                  handleChange("claimant_id_document", file);
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.claimant_id_document;
                    return newErrors;
                  });
                }
              }}
            />
            {errors.claimant_id_document && (
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "0.8rem",
                  textAlign: "left",
                }}
              >
                {errors.claimant_id_document}
              </Typography>
            )}
            <OtpPopup
              open={showOtp}
              otpValue={otpValue}
              onClose={() => setShowOtp(false)}
              onVerify={handleOtpVerify}
              getOtpValue={setOtpValue}
              cellPhoneNumber={cellPhoneNumber}
              onResendOtp={() => sendOtp(cellPhoneNumber)}
            />
            <VerifiedPopup
              label="Verified"
              open={showVerified}
              onClose={handleVerifiedClose}
            />
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                py: 6,
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2 },
              }}
            >
              <ButtonComp
                variant="outlined"
                height={40}
                fontSize="16px"
                width={200}
                onClick={handleBack}
              >
                {string.claims.back}
              </ButtonComp>
              <ButtonComp
                variant="contained"
                height={40}
                fontSize="16px"
                width={200}
                onClick={handleNext}
              >
                {string.claims.next}
              </ButtonComp>
            </Box>
      </Box>
  );
}
